/**
 * @file RefreshEnterpriseUsers.js
 * WHAT: Azure AD (Entra ID) enterprise user and security group synchronization interface with status monitoring, user browsing, and group membership viewing.
 * WHY: DAMA matches database accounts to enterprise identities for attestation routing and account classification; this component keeps that identity data current by syncing from Azure AD.
 */

import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { useSafeTimeout } from '../hooks';
import './RefreshEnterpriseUsers.css';

/**
 * WHAT: Renders a two-tab interface (Users / Groups) for triggering Azure AD syncs, browsing synced users with pagination and filter-by-status, and viewing security group memberships.
 * WHY: Enterprise user data is the identity backbone of DAMA -- attestation campaigns need current employee records, manager hierarchies, and group memberships to route reviews to the right people and classify accounts correctly.
 */
const RefreshEnterpriseUsers = () => {
  const [loading, setLoading] = useState(true);
  const [azureAdStatus, setAzureAdStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState(null);
  const [selectedFilter, setSelectedFilter] = useState('active');
  const [filterTitle, setFilterTitle] = useState('Active Users');
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  // Tabs and Security Groups state
  const [activeTab, setActiveTab] = useState('users'); // 'users' or 'groups'
  const [groups, setGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [syncingGroups, setSyncingGroups] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupMembers, setGroupMembers] = useState([]);
  const [loadingMembers, setLoadingMembers] = useState(false);

  // Safe timeout hook for auto-dismissing messages
  const { setSafeTimeout } = useSafeTimeout();

  useEffect(() => {
    fetchAzureAdStatus();
    // Load active users on page load
    fetchUsersByFilter('active', 'Active Users', true);
  }, []);

  // Fetch users by filter type
  const fetchUsersByFilter = async (filterType, title, isInitialLoad = false) => {
    // If clicking the same filter (and not initial load), toggle off
    if (!isInitialLoad && selectedFilter === filterType) {
      return; // Don't toggle off in split screen mode
    }

    try {
      setLoadingUsers(true);
      setSelectedFilter(filterType);
      setFilterTitle(title);
      setCurrentPage(1);

      const response = await api.enterpriseUsers.getUsers(filterType);

      if (response.data.success) {
        setFilteredUsers(response.data.users);
      } else {
        setFilteredUsers([]);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
      setFilteredUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredUsers.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  // Fetch Azure AD sync status
  const fetchAzureAdStatus = async () => {
    try {
      setLoading(true);
      const response = await api.enterpriseUsers.getAzureAdStatus();
      if (response.data.success) {
        setAzureAdStatus(response.data);
      }
    } catch (err) {
      console.error('Failed to fetch Azure AD status:', err);
    } finally {
      setLoading(false);
    }
  };

  // Trigger Azure AD sync
  const handleAzureAdSync = async () => {
    if (!window.confirm('This will sync all users from Azure Active Directory. This may take several minutes. Continue?')) {
      return;
    }

    try {
      setSyncing(true);
      setSyncMessage({ type: 'info', text: 'Syncing users from Azure AD...' });

      const response = await api.enterpriseUsers.azureAdSync();

      if (response.data.success) {
        const data = response.data.data;
        setSyncMessage({
          type: 'success',
          text: `Successfully synced ${data.users_synced} of ${data.users_fetched} users from Azure AD`
        });

        // Refresh status and user list
        await fetchAzureAdStatus();
        await fetchUsersByFilter(selectedFilter, filterTitle, true);
      } else {
        setSyncMessage({
          type: 'error',
          text: response.data.message || 'Failed to sync from Azure AD'
        });
      }
    } catch (err) {
      console.error('Azure AD sync error:', err);
      setSyncMessage({
        type: 'error',
        text: err.response?.data?.error || 'Failed to sync from Azure AD'
      });
    } finally {
      setSyncing(false);
      setSafeTimeout(() => setSyncMessage(null), 10000);
    }
  };

  // Fetch Azure AD security groups
  const fetchGroups = async (switchTab = true) => {
    try {
      setLoadingGroups(true);
      if (switchTab) {
        setActiveTab('groups');
        setSelectedGroup(null);
        setGroupMembers([]);
        setCurrentPage(1);
      }

      const response = await api.enterpriseUsers.getAzureAdGroups();
      if (response.data.success) {
        setGroups(response.data.groups || []);
      } else {
        setGroups([]);
      }
    } catch (err) {
      console.error('Failed to fetch groups:', err);
      setGroups([]);
    } finally {
      setLoadingGroups(false);
    }
  };

  // Fetch group members
  const fetchGroupMembers = async (group) => {
    try {
      setLoadingMembers(true);
      setSelectedGroup(group);

      const response = await api.enterpriseUsers.getAzureAdGroupMembers(group.group_id);
      if (response.data.success) {
        setGroupMembers(response.data.members || []);
      } else {
        setGroupMembers([]);
      }
    } catch (err) {
      console.error('Failed to fetch group members:', err);
      setGroupMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  // Sync Azure AD security groups
  const handleSyncGroups = async () => {
    if (!window.confirm('This will sync all security groups and memberships from Azure AD. Continue?')) {
      return;
    }

    try {
      setSyncingGroups(true);
      setSyncMessage({ type: 'info', text: 'Syncing security groups from Azure AD...' });

      const response = await api.enterpriseUsers.syncAzureAdGroups();

      if (response.data.success) {
        const data = response.data.data || {};
        setSyncMessage({
          type: 'success',
          text: `Successfully synced ${data.groups_synced || 0} groups and ${data.memberships_synced || 0} memberships from Azure AD`
        });

        // Refresh groups list and status
        await fetchAzureAdStatus();
        if (activeTab === 'groups') {
          await fetchGroups(false);
        }
      } else {
        setSyncMessage({
          type: 'error',
          text: response.data.message || 'Failed to sync groups from Azure AD'
        });
      }
    } catch (err) {
      console.error('Azure AD groups sync error:', err);
      setSyncMessage({
        type: 'error',
        text: err.response?.data?.error || 'Failed to sync groups from Azure AD'
      });
    } finally {
      setSyncingGroups(false);
      setSafeTimeout(() => setSyncMessage(null), 10000);
    }
  };

  // Switch to users tab
  const switchToUsersTab = (filterType = null, title = null) => {
    setActiveTab('users');
    setSelectedGroup(null);
    setGroupMembers([]);
    if (filterType && title) {
      fetchUsersByFilter(filterType, title);
    }
  };

  // Switch to groups tab
  const switchToGroupsTab = () => {
    setActiveTab('groups');
    setSelectedGroup(null);
    setGroupMembers([]);
    if (groups.length === 0) {
      fetchGroups(false);
    }
  };

  // Test Azure AD connection
  const handleTestConnection = async () => {
    try {
      setSyncMessage({ type: 'info', text: 'Testing Azure AD connection...' });

      const response = await api.enterpriseUsers.testAzureAdConnection();

      if (response.data.success) {
        setSyncMessage({
          type: 'success',
          text: `Connected to Azure AD! Found ${response.data.sample_users_count} sample users.`
        });
      } else {
        setSyncMessage({
          type: 'error',
          text: response.data.message || 'Connection test failed'
        });
      }
    } catch (err) {
      console.error('Connection test error:', err);
      setSyncMessage({
        type: 'error',
        text: err.response?.data?.error || 'Failed to connect to Azure AD'
      });
    }

    setSafeTimeout(() => setSyncMessage(null), 10000);
  };

  if (loading) {
    return (
      <div className="refresh-enterprise-users p-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading sync configuration...</p>
      </div>
    );
  }

  return (
    <div className="refresh-enterprise-users">
      <div className="container-fluid p-3" style={{maxWidth: '100%'}}>
        {/* Header Row */}
        <div className="row mb-3">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center flex-wrap">
              <div>
                <h4 className="mb-1">
                  <i className="fas fa-sync-alt me-2"></i>
                  Refresh Enterprise Users and Groups
                </h4>
                <p className="mb-0" style={{color: '#4a5568', fontSize: '0.9rem'}}>
                  Sync user and group data from Azure Active Directory (Entra ID) to DAMA database
                </p>
              </div>
              {azureAdStatus?.azure_configured && (
                <div className="d-flex align-items-center gap-2 mt-2 mt-md-0">
                  <span className="badge bg-success">
                    <i className="fas fa-check-circle me-1"></i>Connected
                  </span>
                  <button
                    className="btn btn-sm btn-warning"
                    onClick={handleTestConnection}
                    disabled={syncing || syncingGroups}
                  >
                    <i className="fas fa-plug me-1"></i>Test
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sync Message Alert */}
        {syncMessage && (
          <div className={`alert alert-${syncMessage.type === 'success' ? 'success' : syncMessage.type === 'error' ? 'danger' : 'info'} alert-dismissible fade show py-2`} role="alert">
            <i className={`fas fa-${syncMessage.type === 'success' ? 'check-circle' : syncMessage.type === 'error' ? 'exclamation-triangle' : 'info-circle'} me-2`}></i>
            {syncMessage.text}
            <button type="button" className="btn-close" onClick={() => setSyncMessage(null)}></button>
          </div>
        )}

        {/* Tabs Navigation with Sync Buttons */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <ul className="nav nav-tabs mb-0">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'users' ? 'active' : ''}`}
                onClick={() => switchToUsersTab()}
              >
                <i className="fas fa-users me-2"></i>
                <strong>Users</strong>
                <span className="badge bg-primary ms-2">{azureAdStatus?.statistics?.total_users || 0}</span>
              </button>
            </li>
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'groups' ? 'active' : ''}`}
                onClick={switchToGroupsTab}
              >
                <i className="fas fa-shield-alt me-2"></i>
                <strong>Groups</strong>
                <span className="badge bg-secondary ms-2">{azureAdStatus?.group_statistics?.total_groups || 0}</span>
              </button>
            </li>
          </ul>
          {azureAdStatus?.azure_configured && (
            <div className="d-flex gap-2">
              <button
                className="btn btn-sm btn-primary"
                onClick={handleAzureAdSync}
                disabled={syncing || syncingGroups || activeTab !== 'users'}
                title={activeTab !== 'users' ? 'Switch to Users tab to sync users' : 'Sync users from Azure AD'}
              >
                {syncing ? (
                  <><span className="spinner-border spinner-border-sm me-1"></span>Syncing...</>
                ) : (
                  <><i className="fas fa-sync-alt me-1"></i>Sync Users</>
                )}
              </button>
              <button
                className="btn btn-sm"
                style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white'}}
                onClick={handleSyncGroups}
                disabled={syncing || syncingGroups || activeTab !== 'groups'}
                title={activeTab !== 'groups' ? 'Switch to Groups tab to sync groups' : 'Sync groups from Azure AD'}
              >
                {syncingGroups ? (
                  <><span className="spinner-border spinner-border-sm me-1"></span>Syncing...</>
                ) : (
                  <><i className="fas fa-sync-alt me-1"></i>Sync Groups</>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === 'users' ? (
        /* Users Tab */
        <div className="row">
          {/* Left Panel - User Stats */}
          <div className="col-12 col-lg-2">
            <div className="card mb-3">
              <div className="card-header bg-primary text-white py-2">
                <h6 className="mb-0"><i className="fas fa-chart-bar me-2"></i>Statistics</h6>
              </div>
              <div className="card-body p-2">
                {azureAdStatus?.statistics ? (
                  <div className="d-flex flex-column gap-2">
                    <div
                      className={`stat-card-vertical ${selectedFilter === 'all' ? 'active' : ''}`}
                      onClick={() => fetchUsersByFilter('all', 'All Users')}
                    >
                      <div className="stat-icon-sm bg-primary"><i className="fas fa-users"></i></div>
                      <div className="stat-info">
                        <div className="stat-value-sm">{azureAdStatus.statistics.total_users.toLocaleString()}</div>
                        <div className="stat-label-sm">Total Users</div>
                      </div>
                    </div>
                    <div
                      className={`stat-card-vertical ${selectedFilter === 'active' ? 'active' : ''}`}
                      onClick={() => fetchUsersByFilter('active', 'Active Users')}
                    >
                      <div className="stat-icon-sm bg-success"><i className="fas fa-check-circle"></i></div>
                      <div className="stat-info">
                        <div className="stat-value-sm">{azureAdStatus.statistics.active_users.toLocaleString()}</div>
                        <div className="stat-label-sm">Active</div>
                      </div>
                    </div>
                    <div
                      className={`stat-card-vertical ${selectedFilter === 'inactive' ? 'active' : ''}`}
                      onClick={() => fetchUsersByFilter('inactive', 'Inactive Users')}
                    >
                      <div className="stat-icon-sm bg-danger"><i className="fas fa-ban"></i></div>
                      <div className="stat-info">
                        <div className="stat-value-sm">{azureAdStatus.statistics.inactive_users.toLocaleString()}</div>
                        <div className="stat-label-sm">Inactive</div>
                      </div>
                    </div>
                    <div
                      className={`stat-card-vertical ${selectedFilter === 'with_managers' ? 'active' : ''}`}
                      onClick={() => fetchUsersByFilter('with_managers', 'Users With Managers')}
                    >
                      <div className="stat-icon-sm bg-info"><i className="fas fa-sitemap"></i></div>
                      <div className="stat-info">
                        <div className="stat-value-sm">{(azureAdStatus.statistics.users_with_managers || 0).toLocaleString()}</div>
                        <div className="stat-label-sm">With Manager</div>
                      </div>
                    </div>
                    <div
                      className={`stat-card-vertical ${selectedFilter === 'without_managers' ? 'active' : ''}`}
                      onClick={() => fetchUsersByFilter('without_managers', 'Users Without Managers')}
                    >
                      <div className="stat-icon-sm bg-warning"><i className="fas fa-user-slash"></i></div>
                      <div className="stat-info">
                        <div className="stat-value-sm">{(azureAdStatus.statistics.users_without_managers || 0).toLocaleString()}</div>
                        <div className="stat-label-sm">No Manager</div>
                      </div>
                    </div>
                    <div className="stat-card-vertical" style={{cursor: 'default', opacity: 0.7}}>
                      <div className="stat-icon-sm bg-secondary"><i className="fas fa-building"></i></div>
                      <div className="stat-info">
                        <div className="stat-value-sm">{(azureAdStatus.statistics.departments || 0).toLocaleString()}</div>
                        <div className="stat-label-sm">Departments</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center text-muted py-3">
                    <i className="fas fa-exclamation-circle"></i>
                    <p className="mb-0 small">No stats available</p>
                  </div>
                )}
              </div>
            </div>

            {/* Last Sync Card */}
            {azureAdStatus?.azure_configured && (
              <div className="card">
                <div className="card-header bg-info text-white py-2">
                  <h6 className="mb-0"><i className="fas fa-clock me-2"></i>Last Sync</h6>
                </div>
                <div className="card-body p-2 text-center">
                  <div style={{color: '#2d3748', fontSize: '0.9rem', fontWeight: 600}}>
                    {azureAdStatus.last_sync_date ? (
                      new Date(azureAdStatus.last_sync_date + 'Z').toLocaleString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })
                    ) : 'Never'}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Right Panel - User List */}
          <div className="col-12 col-lg-10">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center py-2">
                <h6 className="mb-0">
                  <i className="fas fa-list me-2"></i>
                  {filterTitle}
                  {!loadingUsers && <span className="badge bg-secondary ms-2">{filteredUsers.length}</span>}
                </h6>
              </div>
              <div className="card-body p-0">
                {loadingUsers ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted mb-0">Loading users...</p>
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="text-center py-5 text-muted">
                    <i className="fas fa-user-slash fa-2x mb-2"></i>
                    <p className="mb-0">No users found</p>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-sm table-hover mb-0">
                        <thead style={{background: '#f8f9fa'}}>
                          <tr>
                            <th>Employee ID</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Job Title</th>
                            <th>Department</th>
                            <th>Manager</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedUsers.map(user => (
                            <tr key={user.user_id}>
                              <td><code className="text-primary">{user.employee_id || '-'}</code></td>
                              <td>{user.display_name}</td>
                              <td><small>{user.email || '-'}</small></td>
                              <td><small>{user.job_title || '-'}</small></td>
                              <td><small>{user.department || '-'}</small></td>
                              <td>
                                {user.manager_name ? (
                                  <small>{user.manager_name}</small>
                                ) : (
                                  <small className="text-warning"><i className="fas fa-exclamation-triangle me-1"></i>None</small>
                                )}
                              </td>
                              <td>
                                {user.sync_status === 'INACTIVE' || !user.account_enabled ? (
                                  <span className="badge bg-danger">Inactive</span>
                                ) : (
                                  <span className="badge bg-success">Active</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {/* Pagination */}
                    <div className="card-footer d-flex justify-content-between align-items-center py-2">
                      <div className="d-flex align-items-center gap-2">
                        <small className="text-muted">
                          Showing {startIndex + 1}-{Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length}
                        </small>
                        <select
                          className="form-select form-select-sm"
                          style={{width: 'auto'}}
                          value={pageSize}
                          onChange={(e) => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                        >
                          <option value={10}>10 / page</option>
                          <option value={15}>15 / page</option>
                          <option value={25}>25 / page</option>
                          <option value={50}>50 / page</option>
                          <option value={100}>100 / page</option>
                        </select>
                      </div>
                      {totalPages > 1 && (
                        <nav>
                          <ul className="pagination pagination-sm mb-0">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button className="page-link" onClick={() => setCurrentPage(1)} disabled={currentPage === 1}>
                                <i className="fas fa-angle-double-left"></i>
                              </button>
                            </li>
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button className="page-link" onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>
                                <i className="fas fa-angle-left"></i>
                              </button>
                            </li>
                            {[...Array(Math.min(5, totalPages))].map((_, i) => {
                              let pageNum;
                              if (totalPages <= 5) {
                                pageNum = i + 1;
                              } else if (currentPage <= 3) {
                                pageNum = i + 1;
                              } else if (currentPage >= totalPages - 2) {
                                pageNum = totalPages - 4 + i;
                              } else {
                                pageNum = currentPage - 2 + i;
                              }
                              return (
                                <li key={pageNum} className={`page-item ${currentPage === pageNum ? 'active' : ''}`}>
                                  <button className="page-link" onClick={() => setCurrentPage(pageNum)}>
                                    {pageNum}
                                  </button>
                                </li>
                              );
                            })}
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <button className="page-link" onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}>
                                <i className="fas fa-angle-right"></i>
                              </button>
                            </li>
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <button className="page-link" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages}>
                                <i className="fas fa-angle-double-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        ) : (
        /* Groups Tab */
        <div className="row">
          {/* Left Panel - Group Stats */}
          <div className="col-12 col-lg-2">
            <div className="card mb-3">
              <div className="card-header text-white py-2" style={{background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'}}>
                <h6 className="mb-0"><i className="fas fa-chart-bar me-2"></i>Statistics</h6>
              </div>
              <div className="card-body p-2">
                <div className="d-flex flex-column gap-2">
                  <div className="stat-card-vertical" style={{cursor: 'default'}}>
                    <div className="stat-icon-sm" style={{background: '#667eea'}}><i className="fas fa-layer-group"></i></div>
                    <div className="stat-info">
                      <div className="stat-value-sm">{(azureAdStatus?.group_statistics?.total_groups || 0).toLocaleString()}</div>
                      <div className="stat-label-sm">Total Groups</div>
                    </div>
                  </div>
                  <div className="stat-card-vertical" style={{cursor: 'default'}}>
                    <div className="stat-icon-sm bg-success"><i className="fas fa-check-circle"></i></div>
                    <div className="stat-info">
                      <div className="stat-value-sm">{(azureAdStatus?.group_statistics?.active_groups || 0).toLocaleString()}</div>
                      <div className="stat-label-sm">Active</div>
                    </div>
                  </div>
                  <div className="stat-card-vertical" style={{cursor: 'default'}}>
                    <div className="stat-icon-sm bg-info"><i className="fas fa-users"></i></div>
                    <div className="stat-info">
                      <div className="stat-value-sm">{(azureAdStatus?.group_statistics?.users_in_groups || 0).toLocaleString()}</div>
                      <div className="stat-label-sm">Memberships</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Last Group Sync Card */}
            <div className="card">
              <div className="card-header bg-secondary text-white py-2">
                <h6 className="mb-0"><i className="fas fa-clock me-2"></i>Last Sync</h6>
              </div>
              <div className="card-body p-2 text-center">
                <div style={{color: '#2d3748', fontSize: '0.9rem', fontWeight: 600}}>
                  {azureAdStatus?.group_statistics?.last_group_sync_date ? (
                    new Date(azureAdStatus.group_statistics.last_group_sync_date + 'Z').toLocaleString(undefined, {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })
                  ) : 'Never'}
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel - Groups List */}
          <div className="col-12 col-lg-10">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center py-2">
                <h6 className="mb-0">
                  <i className="fas fa-shield-alt me-2"></i>
                  {selectedGroup ? (
                    <>
                      <span className="text-muted">Groups /</span> {selectedGroup.group_name}
                      <span className="badge bg-secondary ms-2">{groupMembers.length} members</span>
                    </>
                  ) : (
                    <>Security Groups<span className="badge bg-secondary ms-2">{groups.length}</span></>
                  )}
                </h6>
                {selectedGroup && (
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => { setSelectedGroup(null); setGroupMembers([]); }}
                  >
                    <i className="fas fa-arrow-left me-1"></i>Back to Groups
                  </button>
                )}
              </div>
              <div className="card-body p-0">
                {loadingGroups || loadingMembers ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted mb-0">Loading {selectedGroup ? 'members' : 'groups'}...</p>
                  </div>
                ) : selectedGroup ? (
                  /* Group Members View */
                  groupMembers.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="fas fa-users-slash fa-2x mb-2"></i>
                      <p className="mb-0">No members in this group</p>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm table-hover mb-0">
                        <thead style={{background: '#f8f9fa'}}>
                          <tr>
                            <th>Employee ID</th>
                            <th>Name</th>
                            <th>Email</th>
                            <th>Job Title</th>
                            <th>Department</th>
                            <th>Membership Type</th>
                            <th>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groupMembers.map(member => (
                            <tr key={member.user_id}>
                              <td><code className="text-primary">{member.employee_id || '-'}</code></td>
                              <td>{member.display_name}</td>
                              <td><small>{member.email || '-'}</small></td>
                              <td><small>{member.job_title || '-'}</small></td>
                              <td><small>{member.department || '-'}</small></td>
                              <td><span className="badge bg-info">{member.membership_type || 'Direct'}</span></td>
                              <td>
                                {member.account_enabled ? (
                                  <span className="badge bg-success">Active</span>
                                ) : (
                                  <span className="badge bg-danger">Inactive</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                ) : (
                  /* Groups List View */
                  groups.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="fas fa-layer-group fa-2x mb-2"></i>
                      <p className="mb-0">No security groups found</p>
                      <small>Click "Sync Groups" to fetch groups from Azure AD</small>
                    </div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-sm table-hover mb-0">
                        <thead style={{background: '#f8f9fa'}}>
                          <tr>
                            <th>Group Name</th>
                            <th>Description</th>
                            <th>Members</th>
                            <th>Owner</th>
                            <th>Last Sync</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {groups.map(group => (
                            <tr key={group.group_id}>
                              <td>
                                <strong style={{color: '#667eea', cursor: 'pointer'}} onClick={() => fetchGroupMembers(group)}>
                                  <i className="fas fa-shield-alt me-1"></i>{group.group_name}
                                </strong>
                              </td>
                              <td><small className="text-muted">{group.description || '-'}</small></td>
                              <td>
                                <span className="badge bg-secondary">{group.member_count}</span>
                              </td>
                              <td><small>{group.owner_name || '-'}</small></td>
                              <td>
                                <small>
                                  {group.last_sync_date ? (
                                    new Date(group.last_sync_date + 'Z').toLocaleDateString(undefined, {
                                      month: 'short',
                                      day: 'numeric'
                                    })
                                  ) : '-'}
                                </small>
                              </td>
                              <td>
                                {group.is_active ? (
                                  <span className="badge bg-success">Active</span>
                                ) : (
                                  <span className="badge bg-secondary">Inactive</span>
                                )}
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => fetchGroupMembers(group)}
                                  title="View Members"
                                >
                                  <i className="fas fa-users"></i>
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                )}
              </div>
            </div>
          </div>
        </div>
        )}

      </div>
    </div>
  );
};

export default RefreshEnterpriseUsers;
